import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from '../../../hooks/useTranslation';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ButtonContent = styled('span')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '4px',
});

const FilenameBadge = styled('span')({
  fontSize: '12px',
  color: 'rgb(23, 65, 255)',
  fontWeight: 500,
  maxWidth: '200px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

interface InputFileUploadProps {
  name: string;
}

const InputFileUpload: React.FC<InputFileUploadProps> = ({ name }) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const { field } = useController({ name, control });
  const [filename, setFilename] = useState<string>('');

  // Derive display name from existing URL (for edit workflow)
  const displayName = useMemo(() => {
    if (filename) return filename;
    if (!field.value) return '';

    // Handle remote URLs - extract filename from path
    if (field.value.startsWith('http')) {
      try {
        const url = new URL(field.value);
        const pathSegments = url.pathname.split('/');
        const lastSegment = pathSegments[pathSegments.length - 1];
        return lastSegment
          ? decodeURIComponent(lastSegment)
          : t('Uploaded image');
      } catch {
        return t('Uploaded image');
      }
    }

    return '';
  }, [filename, field.value, t]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFilename(file.name);
      setValue(`${name}_filename`, file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        field.onChange(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Button
      component="label"
      role={undefined}
      variant="outlined"
      tabIndex={-1}
      startIcon={<CloudUploadIcon />}
      sx={{
        display: 'flex !important',
        height: '56px',
        justifyContent: 'center',
        color: 'rgb(23, 65, 255)',
        borderColor: 'rgb(23, 65, 255)',
        '&:hover': {
          borderColor: 'rgb(23, 65, 255)',
          backgroundColor: 'rgba(23, 65, 255, 0.04)',
        },
      }}
    >
      {!displayName && <span>{t('Upload')}</span>}
      {displayName && <FilenameBadge>{displayName}</FilenameBadge>}
      <VisuallyHiddenInput
        type="file"
        onChange={handleFileChange}
        accept="image/*"
      />
    </Button>
  );
};

export default InputFileUpload;
